import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AirplaneIcon from './AirplaneIcon';

const NAV_LINKS = [
  { label: 'Plan My Day', href: '/day-planner' },
  { label: 'Pricing', href: '/membership' },
  { label: 'Gift', href: '/membership' },
  { label: 'Journal', href: '/journal' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
];

const SiteNav: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <nav
      aria-label="Main navigation"
      className="fixed top-0 left-0 w-full z-50 px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex justify-between items-center transition-all duration-500 text-white"
      style={{
        background: scrollY > 50 ? 'rgba(10, 10, 10, 0.65)' : 'rgba(10, 10, 10, 0.25)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      }}
    >
      {/* Logo */}
      <Link
        to="/"
        className="flex items-center gap-3 group cursor-pointer"
        aria-label="Aura Traveler home"
      >
        <AirplaneIcon className="w-6 h-6 text-white group-hover:text-gold transition-colors duration-500" />
        <div className="flex flex-col">
          <span className="font-display text-xl tracking-[0.3em] uppercase leading-none text-white">
            Aura
          </span>
          <span className="font-serif text-[10px] italic tracking-[0.4em] text-gold/60 mt-0.5 uppercase">
            Traveler
          </span>
        </div>
      </Link>

      {/* Desktop Nav */}
      <div className="hidden md:flex items-center gap-10">
        {NAV_LINKS.map(({ label, href }) => (
          <Link
            key={href}
            to={href}
            className={`text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
              isActive(href) ? 'text-gold' : 'text-white/80 hover:text-gold'
            }`}
          >
            {label}
          </Link>
        ))}
        <Link
          to="/create"
          aria-label="Create Itinerary"
          className="px-6 py-2.5 bg-gold text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-full hover:bg-white transition-all duration-500 shadow-lg shadow-gold/10 press-haptic inline-flex items-center"
        >
          Create Itinerary
        </Link>
      </div>

      {/* Mobile: hamburger + CTA — always visible top-right, no matter what */}
      <div className="md:hidden flex items-center gap-3">
        <Link
          to="/create"
          aria-label="Create Itinerary"
          className="px-4 py-2.5 bg-gold text-charcoal text-[10px] font-bold uppercase tracking-[0.12em] rounded-full min-h-[44px] inline-flex items-center whitespace-nowrap"
        >
          Create
        </Link>
        <button
          aria-label="Toggle menu"
          className="w-11 h-11 flex flex-col justify-center items-center gap-1.5"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span
            className={`block w-6 h-px bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[5px]' : ''}`}
          />
          <span
            className={`block w-6 h-px bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[3px]' : ''}`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute top-full left-0 w-full rounded-none py-6 flex flex-col items-center gap-4 md:hidden"
            style={{
              background: 'rgba(10, 10, 10, 0.85)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            }}
          >
            {NAV_LINKS.map(({ label, href }) => (
              <Link
                key={href}
                to={href}
                className={`text-[11px] font-semibold uppercase tracking-[0.3em] min-h-[44px] flex items-center transition-colors duration-300 ${
                  isActive(href) ? 'text-gold' : 'text-white/80'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>
        </>
      )}
    </nav>
  );
};

export default SiteNav;
